import type { BBox, LonLat } from "../../api/types";
import { isPlacedLevel, type PlacedLevel } from "../../echelons";
import { k } from "../../i18n/i18n";
import { forLevel, LEVEL_KEYS, WIDTHS } from "../manoeuvre/corridorWidths";
import { helperDistance } from "../manoeuvre/corridorFrame";

/** Empreinte préparée d'une zone libre, calculée en direct pendant le tracé de l'axe (docs/14 §2.7, §6.2) : jumeau de
 *  `generex_api/zones/footprint.py`, vérifié par le même jeu de vecteurs (`manoeuvre/__fixtures__/footprint.json`).
 *
 *  Tampon orienté du fuseau tracé (axe de l'arrière vers l'ennemi, largeur doctrinale de la clé de gabarit), élargi
 *  de e = max(10 km ; plus grande demi-largeur par défaut des familles du niveau) : un rectangle de
 *  (W + 2e) × (L + 2e) posé dans le plan équirectangulaire du milieu de l'axe. Contrôles dans l'ordre du serveur :
 *  niveau, extrémités dans le monde admis, antiméridien, longueur, coins, plafond de surface du niveau, grille D1 de
 *  l'emprise englobante (190 × 190 cases de 1 893 × 1 810 m au plus).
 *
 *  Extension (`extensionPreview`) : union de l'empreinte et d'une emprise nord-sud. Le serveur mesure la surface
 *  géodésique de l'union (WGS 84) ; le client l'estime dans une projection sinusoïdale (à surfaces conservées), à
 *  moins de 1 % près sur les emprises admises : l'aperçu, pas la décision. */

const R_M = 6_378_137;
/** Rayon authalique de WGS 84 : surfaces de l'aperçu d'extension. */
const R_AUTHALIC_M = 6_371_007.2;
export const MIN_AXIS_M = 5_000;
export const MAX_AXIS_M = 200_000;
const MIN_MARGIN_M = 10_000;
export const WORLD: BBox = [-179.5, -79.5, 179.5, 83.5];
export type ZoneLevel = PlacedLevel;
export const AREA_CAP_KM2: Readonly<Record<ZoneLevel, number>> = {
  REGIMENT: 40_000,
  BRIGADE: 40_000,
  DIVISION: 55_000,
  CORPS: 300_000,
  COMPAGNIE: 40_000,
};
export const D1_CELL_W_M = 1893;
export const D1_CELL_H_M = 1810;
export const D1_MAX_CELLS = 190;

/** Échelle propre d'un terrain de corps d'armée (docs/08 §23, `footprint.SCALES` du serveur) : marge de 25 km, axe de
 *  100 à 800 km, cases D1 doublées (au plus 260 × 260) ; les autres niveaux n'en ont pas. */
export interface Scale {
  margin_m: number;
  min_axis_m: number;
  max_axis_m: number;
  d1_cell_m: [number, number];
  d1_max_cells: number;
}
export const SCALES: Readonly<Partial<Record<ZoneLevel, Scale>>> = {
  CORPS: { margin_m: 25_000, min_axis_m: 100_000, max_axis_m: 800_000, d1_cell_m: [2 * D1_CELL_W_M, 2 * D1_CELL_H_M], d1_max_cells: 260 },
};

/** Longueurs d'axe admises au niveau (m). */
export function axisBounds(level: ZoneLevel): [number, number] {
  const scale = SCALES[level];
  return scale ? [scale.min_axis_m, scale.max_axis_m] : [MIN_AXIS_M, MAX_AXIS_M];
}

/** Motifs de refus, textes identiques à ceux du serveur (traduits à l'affichage). */
export const FOOTPRINT_ERRORS = {
  level: k("Choisissez d'abord le niveau de l'ordre"),
  length: k("Longueur admise : 5 à 200 km"),
  lengthCorps: k("Longueur admise au niveau corps d'armée : 100 à 800 km"),
  world: k("Terrain hors des limites admises : de 79,5° S à 83,5° N, sans franchir l'antiméridien"),
  tooLarge: k("Terrain trop étendu pour ce niveau"),
  detached: k("L'extension doit toucher le terrain préparé"),
  invalid: k("Emprise d'extension invalide"),
} as const;
export type FootprintError = keyof typeof FOOTPRINT_ERRORS;

export interface Footprint {
  /** Anneau fermé [lon, lat] (arrière droit, avant droit, avant gauche, arrière gauche, arrière droit). */
  ring: LonLat[];
  bbox: BBox;
  area_km2: number;
  length_m: number;
  width_m: number;
  margin_m: number;
}

/** Résultat du calcul : l'empreinte, ou le motif du refus avec ce qui a pu être mesuré (la puce du tracé affiche la
 *  longueur et la surface même hors bornes). */
export type FootprintResult =
  | { ok: true; footprint: Footprint }
  | { ok: false; error: FootprintError; length_m?: number; area_km2?: number; footprint?: Footprint };

export const isZoneLevel = (level: unknown): level is ZoneLevel => isPlacedLevel(level);

/** Élargissement du tampon : max(10 km ; plus grande demi-largeur par défaut des familles du niveau) ; corps d'armée :
 *  la marge de son échelle (25 km) ; régiment : la demi-largeur de sa famille (`levelKey`, inconnue : l'infanterie),
 *  depuis le 26/10/2026 (`margin_m` du serveur). */
export function marginM(level: ZoneLevel, levelKey?: string | null): number {
  const scale = SCALES[level];
  if (scale) return scale.margin_m;
  if (level === "REGIMENT") return Math.max(MIN_MARGIN_M, corridorWidthM(level, levelKey) / 2);
  return Math.max(MIN_MARGIN_M, ...LEVEL_KEYS[level]!.map((key) => WIDTHS[key].default_m / 2));
}

/** Largeur du fuseau tracé : largeur par défaut de la clé de gabarit (inconnue : la première famille du niveau). */
export function corridorWidthM(level: ZoneLevel, levelKey?: string | null): number {
  return forLevel(level, levelKey)!.default_m;
}

/** Longueur de l'axe (haversine sur 6 378 137 m, celle de la validation du fuseau). */
export const axisLengthM = (start: LonLat, end: LonLat) => helperDistance(start, end);

const inWorld = ([lon, lat]: LonLat) =>
  Number.isFinite(lon) && Number.isFinite(lat) && WORLD[0] <= lon && lon <= WORLD[2] && WORLD[1] <= lat && lat <= WORLD[3];

/** (colonnes, lignes) de la grille D1 qui couvre l'emprise : largeur à la latitude du milieu, hauteur sur le méridien ;
 *  `cell` : cases d'une échelle propre (corps d'armée). */
export function d1Dims([w, s, e, n]: BBox, cell: [number, number] = [D1_CELL_W_M, D1_CELL_H_M]): [number, number] {
  const k = Math.PI / 180;
  const width = (e - w) * R_M * Math.cos(((s + n) / 2) * k) * k;
  const height = (n - s) * R_M * k;
  return [Math.ceil(width / cell[0] - 1e-9), Math.ceil(height / cell[1] - 1e-9)];
}

const dimsTooLarge = (bbox: BBox, level?: ZoneLevel) => {
  const scale = level ? SCALES[level] : undefined;
  return d1Dims(bbox, scale?.d1_cell_m).some((n) => n > (scale?.d1_max_cells ?? D1_MAX_CELLS));
};
const round7 = (v: number) => Math.round(v * 1e7) / 1e7;

/** Empreinte préparée d'un axe tracé ([lon, lat], de l'arrière vers l'ennemi) au niveau `level`. */
export function preparedFootprint(
  start: LonLat,
  end: LonLat,
  level: string | null | undefined,
  levelKey?: string | null,
): FootprintResult {
  if (!isZoneLevel(level)) return { ok: false, error: "level" };
  if (!inWorld(start) || !inWorld(end)) return { ok: false, error: "world" };
  // Le plus court chemin franchit l'antiméridien : le milieu tomberait à l'opposé du globe.
  if (Math.abs(end[0] - start[0]) > 180) return { ok: false, error: "world" };
  const length = axisLengthM(start, end);
  const width = corridorWidthM(level, levelKey);
  const margin = marginM(level, levelKey);
  const area = ((width + 2 * margin) * (length + 2 * margin)) / 1e6;
  const [low, high] = axisBounds(level);
  if (!(low <= length && length <= high))
    return { ok: false, error: SCALES[level] ? "lengthCorps" : "length", length_m: length, area_km2: area };
  const lat0 = (start[1] + end[1]) / 2;
  const lon0 = (start[0] + end[0]) / 2;
  const k = Math.PI / 180;
  const mx = R_M * Math.cos(lat0 * k) * k;
  const my = R_M * k;
  const dx = (end[0] - start[0]) * mx;
  const dy = (end[1] - start[1]) * my;
  const norm = Math.hypot(dx, dy);
  const ux = dx / norm;
  const uy = dy / norm; // le long de l'axe, vers l'ennemi
  const nx = -uy;
  const ny = ux; // à gauche de l'axe
  const halfLen = length / 2 + margin;
  const halfWid = width / 2 + margin;
  const corners: LonLat[] = (
    [
      [-1, -1],
      [1, -1],
      [1, 1],
      [-1, 1],
    ] as const
  ).map(([a, b]) => {
    const x = a * halfLen * ux + b * halfWid * nx;
    const y = a * halfLen * uy + b * halfWid * ny;
    return [lon0 + x / mx, lat0 + y / my];
  });
  if (!corners.every(inWorld)) return { ok: false, error: "world", length_m: length, area_km2: area };
  const lons = corners.map((p) => p[0]);
  const lats = corners.map((p) => p[1]);
  const footprint: Footprint = {
    ring: [...corners, corners[0]!].map(([lon, lat]) => [round7(lon), round7(lat)]),
    bbox: [Math.min(...lons), Math.min(...lats), Math.max(...lons), Math.max(...lats)],
    area_km2: area,
    length_m: length,
    width_m: width,
    margin_m: margin,
  };
  if (area > AREA_CAP_KM2[level] || dimsTooLarge(footprint.bbox, level))
    return { ok: false, error: "tooLarge", length_m: length, area_km2: area, footprint };
  return { ok: true, footprint };
}

/** Rectangle orienté sur l'axe (plan équirectangulaire du milieu), prolongé de `extendM` aux deux bouts et large de
 *  2 × `halfWidthM` : fuseau prévu (tirets) pendant le tracé. Anneau fermé [lon, lat]. */
export function orientedRing(start: LonLat, end: LonLat, extendM: number, halfWidthM: number): LonLat[] {
  const lat0 = (start[1] + end[1]) / 2;
  const lon0 = (start[0] + end[0]) / 2;
  const k = Math.PI / 180;
  const mx = R_M * Math.cos(lat0 * k) * k;
  const my = R_M * k;
  const dx = (end[0] - start[0]) * mx;
  const dy = (end[1] - start[1]) * my;
  const norm = Math.hypot(dx, dy) || 1;
  const [ux, uy] = [dx / norm, dy / norm];
  const halfLen = norm / 2 + extendM;
  const corners = ([[-1, -1], [1, -1], [1, 1], [-1, 1]] as const).map(([a, b]): LonLat => [
    lon0 + (a * halfLen * ux - b * halfWidthM * uy) / mx,
    lat0 + (a * halfLen * uy + b * halfWidthM * ux) / my,
  ]);
  return [...corners, corners[0]!];
}

// --- Extension --------------------------------------------------------------------------------------------------

/** Aire (km²) d'un anneau [lon, lat] dans la projection sinusoïdale centrée sur `lon0` (surfaces conservées). */
function ringAreaKm2(ring: LonLat[], lon0: number): number {
  const k = Math.PI / 180;
  const pts = ring.map(([lon, lat]) => [R_AUTHALIC_M * (lon - lon0) * k * Math.cos(lat * k), R_AUTHALIC_M * lat * k]);
  let sum = 0;
  for (let i = 0; i < pts.length; i++) {
    const [x1, y1] = pts[i]!;
    const [x2, y2] = pts[(i + 1) % pts.length]!;
    sum += x1! * y2! - x2! * y1!;
  }
  return Math.abs(sum) / 2 / 1e6;
}

/** Découpe d'un polygone (quelconque) par une emprise nord-sud (Sutherland-Hodgman) : la surface du résultat est
 *  celle de l'intersection. */
export function clipToBox(ring: LonLat[], [w, s, e, n]: BBox): LonLat[] {
  const open = ring.length > 1 && ring[0]![0] === ring.at(-1)![0] && ring[0]![1] === ring.at(-1)![1] ? ring.slice(0, -1) : ring;
  const edges: { inside: (p: LonLat) => boolean; cut: (a: LonLat, b: LonLat) => LonLat }[] = [
    { inside: (p) => p[0] >= w, cut: (a, b) => [w, a[1] + ((b[1] - a[1]) * (w - a[0])) / (b[0] - a[0])] },
    { inside: (p) => p[0] <= e, cut: (a, b) => [e, a[1] + ((b[1] - a[1]) * (e - a[0])) / (b[0] - a[0])] },
    { inside: (p) => p[1] >= s, cut: (a, b) => [a[0] + ((b[0] - a[0]) * (s - a[1])) / (b[1] - a[1]), s] },
    { inside: (p) => p[1] <= n, cut: (a, b) => [a[0] + ((b[0] - a[0]) * (n - a[1])) / (b[1] - a[1]), n] },
  ];
  let out = open;
  for (const edge of edges) {
    const input = out;
    out = [];
    for (let i = 0; i < input.length; i++) {
      const current = input[i]!;
      const previous = input[(i + input.length - 1) % input.length]!;
      const inCurrent = edge.inside(current);
      const inPrevious = edge.inside(previous);
      if (inCurrent) {
        if (!inPrevious) out.push(edge.cut(previous, current));
        out.push(current);
      } else if (inPrevious) out.push(edge.cut(previous, current));
    }
    if (out.length === 0) break;
  }
  return out;
}

export interface ExtensionPreview {
  ok: boolean;
  error?: FootprintError;
  /** Surface estimée de l'union (km²). */
  area_km2: number;
  /** Surface ajoutée (km²), comptée dans le quota. */
  added_km2: number;
  bbox: BBox;
}

/** Aperçu d'une extension : emprise valide, recouvrement réel de l'empreinte (un contact par un sommet ou un côté
 *  est refusé), surface de l'union sous le plafond du niveau, grille D1 de l'emprise englobante. */
export function extensionPreview(ring: LonLat[], box: BBox, level: string | null | undefined): ExtensionPreview {
  const [w, s, e, n] = box;
  const lons = ring.map((p) => p[0]);
  const lats = ring.map((p) => p[1]);
  const union: BBox = [Math.min(w, ...lons), Math.min(s, ...lats), Math.max(e, ...lons), Math.max(n, ...lats)];
  const lon0 = (union[0] + union[2]) / 2;
  const base = ringAreaKm2(ring, lon0);
  const extra = ringAreaKm2(
    [
      [w, s],
      [e, s],
      [e, n],
      [w, n],
    ],
    lon0,
  );
  const clipped = clipToBox(ring, box);
  const overlap = clipped.length >= 3 ? ringAreaKm2(clipped, lon0) : 0;
  const area = base + extra - overlap;
  const result = (error?: FootprintError): ExtensionPreview => ({
    ok: !error,
    error,
    area_km2: area,
    added_km2: Math.max(0, area - base),
    bbox: union,
  });
  if (!isZoneLevel(level)) return result("level");
  if (![w, s, e, n].every(Number.isFinite) || !(w < e && s < n)) return result("invalid");
  if (!inWorld([w, s]) || !inWorld([e, n])) return result("world");
  if (overlap <= 1e-9) return result("detached");
  if (area > AREA_CAP_KM2[level] || dimsTooLarge(union, level)) return result("tooLarge");
  return result();
}

/** Emprise nord-sud de deux coins opposés (dans n'importe quel ordre). */
export function boxOf(a: LonLat, b: LonLat): BBox {
  return [Math.min(a[0], b[0]), Math.min(a[1], b[1]), Math.max(a[0], b[0]), Math.max(a[1], b[1])];
}
